import { Component, OnInit, OnDestroy, Input, Output, ViewChild, ViewEncapsulation, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { CustomerCategory } from '../../shared/client';
import { ClientApiService } from '../../services/client-api.service';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

/**
 * Reusable customer-category (company group) dropdown selector backed by `ClientApiService`.
 *
 * On init the component loads the full `CustomerCategory` list and resolves the initial selection
 * from `selectedID`. The list is also reloaded after a new category is created via `onCategoryAddButton`.
 *
 * On `selectedID` or `groupname` input changes the component re-resolves `_selectedCategory` from
 * the already-loaded list without a new HTTP call.
 *
 * The `groupname` input is dual-purpose: it seeds the text field for creating a new category via
 * `onCategoryAddButton`, and changes to it trigger a re-selection attempt (in case the parent
 * component is reflecting a name change from elsewhere in the form).
 *
 * **Output:** `selectedCategoryID` emits the selected `CASTCATID` on change, clear, and after add.
 */
@Component({
  selector: 'app-customercategoryselector',
  templateUrl: './customercategoryselector.component.html',
  styleUrls: ['./customercategoryselector.component.css'],
  encapsulation: ViewEncapsulation.None

})
export class CustomercategoryselectorComponent implements OnInit, OnChanges, OnDestroy {

  _castCatList: CustomerCategory[];

  /** Currently resolved `CustomerCategory` object; set internally by `_selectCategory`. */
  @Input() _selectedCategory: CustomerCategory;

  _loading: boolean = false;

  /** Label text displayed above the dropdown. */
  @Input() labelKey: string = 'Группа компаний';

  /** Placeholder text shown inside the dropdown when nothing is selected. */
  @Input() placeholder: string = 'Выбрать группу';

  /** Text field value used as the name when creating a new category via `onCategoryAddButton`. */
  @Input() groupname: string;

  /** The ID of the selected category (`CustomerCategory.CASTCATID`). Triggers re-selection on change. */
  @Input() selectedID: number = null;

  /** When `true`, the dropdown is rendered as read-only. */
  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(b: boolean) {
    this._disabled = b;
  }
  _disabled: boolean = false;

  /** Emits the selected `CASTCATID` on change, on clear, and after a new category is created. */
  @Output() public selectedCategoryID: EventEmitter<number> = new EventEmitter();

  private _addCustCatQuery: Subscription;
  private _getCustCatQuery: Subscription;

  /** `true` if a category is currently selected (`selectedID` is non-null). */
  public get isThereSelection(): boolean { return (this.selectedID ? true : false)};

  /** `true` when the Add Group button should be disabled: name is blank or already exists in the list. */
  public get cannotAddGroup(): boolean {
    return ((this.groupname.trim() == "") || ((this._castCatList || []).find(x => x.CASTCATNAME == this.groupname) != null))
  };

  /** `true` when the groupname field differs from the currently selected category's name, indicating a pending new-group input. */
  public get allowClear(): boolean { return (this.groupname != (this._selectedCategory ? this._selectedCategory.CASTCATNAME : '')) };

  constructor(private cliapi: ClientApiService) { }

  /** Resolves `_selectedCategory` from `_castCatList` based on the current `selectedID`. */
  private _selectCategory(): void {
    this._selectedCategory = (this.selectedID && this._castCatList) ? this._castCatList.find(x => x.CASTCATID == this.selectedID) : null;
    console.log('*** CustomercategoryselectorComponent _selectCategory: ' + this._selectedCategory);
  }

  /** Fetches the full category list from the API and re-resolves `_selectedCategory`. */
  private _reloadComponentData(): void {
    this._loading = true;

    this._getCustCatQuery = this.cliapi.getCustomerCategoryList()
      .pipe(map(data => {
        this._castCatList = data;
        this._loading = false;
        this._selectCategory();
        console.log('*** CustomercategoryselectorComponent loaded');
      }))
        .subscribe();
  }

  /** Reacts to `selectedID` and `groupname` changes by re-resolving `_selectedCategory` from the cached list. */
  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      //console.log('*** CustomercategoryselectorComponent ngOnChanges');
      let change = changes[propName];

      //console.log('*** CustomercategoryselectorComponent ngOnChanges (was):    ' + propName + ' ' + JSON.stringify(change.previousValue));
      console.log('*** CustomercategoryselectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));

      switch (propName) {
        case 'selectedID':
          this._selectCategory();
          break;

        case 'groupname':
          if (change.currentValue && this._selectedCategory && (change.currentValue != this._selectedCategory.CASTCATNAME)) {
            /* TODO: We need to reflect the changes ???? */
          }
          this._selectCategory();
          break;
      }
    }
  }

  /** Unsubscribes all active HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    if (this._addCustCatQuery) { this._addCustCatQuery.unsubscribe(); }
    if (this._getCustCatQuery) { this._getCustCatQuery.unsubscribe(); }
  }

  /** Triggers the initial category list load. */
  ngOnInit(): void {
    console.log('*** CustomercategoryselectorComponent ngOnInit: ');
    this._reloadComponentData();
  }

  /** Handles PrimeNG dropdown `(onClick)` — currently no-op (body is commented out). */
  onClickCS(e) {
    //console.log('*** CustomercategoryselectorComponent onClickCS: ' + JSON.stringify(e))

    //if (this._selectedCategory) {
    //  this.selectedID = this._selectedCategory.CASTCATID;
    //  this.selectedCategoryID.emit(this.selectedID);
    //}

  //  this.selectedID = this._selectedCategory ? this._selectedCategory.CASTCATID : null;
  //  this.selectedCategoryID.emit(this.selectedID);
  }

  /** Handles PrimeNG dropdown `(onChange)` — syncs `selectedID` from `_selectedCategory` and emits `selectedCategoryID`. */
  onChangeCS(e) {
    console.log('*** CustomercategoryselectorComponent onChangeCS: ' + JSON.stringify(e));

    // this._selectedCategory = e.value;
    //this.selectedID = this._selectedCategory ? this._selectedCategory.CASTCATID : null;
    //this.selectedCategoryID.emit(this.selectedID);

    if (this._selectedCategory) {
      this.selectedID = this._selectedCategory.CASTCATID;
      this.selectedCategoryID.emit(this.selectedID);
    }
  }

  /** Handles PrimeNG dropdown `(onClear)` — sets `selectedID` to `null` and emits `selectedCategoryID`. */
  onClearCS(e) {
    console.log('*** CustomercategoryselectorComponent onClearCS: ' + JSON.stringify(e));

    this.selectedID = null;
    this.selectedCategoryID.emit(this.selectedID);
  }

  /**
   * Creates a new `CustomerCategory` using `groupname` as the name, then reloads the list and emits
   * the new category's ID. Guard: only fires if `allowClear` is true (name differs from selection).
   */
  onCategoryAddButton(e) {
    console.log('*** CustomercategoryselectorComponent onCategoryAddButton: ' + JSON.stringify(e));
    //console.log('*** CustomercategoryselectorComponent onCategoryAddButton: about to add group ' + this.groupname);

    if (this.allowClear) {

      this._addCustCatQuery = this.cliapi.postCustomerCategoryByID({ CASTCATID: null, CASTCATNAME: this.groupname, CASTCATCOMM: 'Группа компаний ' + this.groupname })
        .pipe(
          map(cc => this.selectedID = cc.CASTCATID),

          finalize(() => {
            this._reloadComponentData();
            this.selectedCategoryID.emit(this.selectedID);
        }))
        .subscribe();
    }
  }

  /** Placeholder for editing a category — not yet implemented (body is a TODO comment). */
  onCategoryEditButton(e) {
    console.log('*** CustomercategoryselectorComponent onCategoryEditButton: ' + JSON.stringify(e));

    /*
     * TODO: open dialog to edit CutomerCategory
     *
     * But for what?
     * Very dangerous action to change the CastCatName.
     * 
     */ 

  }

  /** PrimeNG dropdown `(onFilter)` handler — currently no-op. */
  onFilterOptions(e) {
    //console.log('*** CustomercategoryselectorComponent onFilterOptions: ' + JSON.stringify(e));
  }
}
